import { types as api } from '@mesa-labs/mesa-api';
import { ExternalVendorApi } from '@mesa-labs/mesa-api/dist/apis/externalVendor';
import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

import {
  getIdToken,
  prepareHeaders,
  ExternalVendorStatisticsMutex,
  useApi,
} from './api';
import { appendFilterParams } from '../../utils';

export const externalVendorsApi = createApi({
  reducerPath: 'externalVendorsApi',
  baseQuery: fetchBaseQuery({
    baseUrl: CONFIG.api.externalVendorUrl,
    prepareHeaders,
  }),
  tagTypes: ['ExternalVendors', 'ExternalVendor', 'ExternalVendorClients', 'ExternalVendorInvoices', 'ExternalVendorDimensions', 'ExternalClients', 'ExternalClient', 'ExternalClientVendors', 'AllExternalClientVendors', 'ExternalClientInvoices', 'TaskSchedules', 'TaskExecutions', 'MatchingDashboard'],
  endpoints: (build) => ({
    getExternalVendors: build.query<readonly api.ExternalVendorDataResponse[], { externalVendorIdentifiers: readonly api.ExternalVendorIdentifier[]; }>({
      async queryFn({ externalVendorIdentifiers }) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorIdentifiers.reduce((p, externalVendorIdentifier) => p.then(async (res) => {
          const externalVendor = await externalVendorApi.getExternalVendor(externalVendorIdentifier.partnerId, externalVendorIdentifier.externalVendorId);
          return externalVendor ? [...res, externalVendor] : res;
        }), Promise.resolve([] as api.ExternalVendorDataResponse[]));
        return { data };
      },
    }),
    getExternalVendorSpend:
      build.query<api.ExternalVendorSpendResponse, api.ExternalVendorSpendFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendorSpend(params);
          return { data };
        },
        providesTags: ['ExternalVendors'],
      }),
    getExternalVendorOnboardedSpend:
      build.query<api.InternalVendorSpendResponse, api.InternalVendorSpendFilterParams>({
        async queryFn(params) {
          return await ExternalVendorStatisticsMutex.runExclusive(async () => {
            const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
            const data = await externalVendorApi.getOnboardedSpend(params);
            return { data };
          });
        },
        providesTags: ['ExternalVendors'],
      }),
    getExternalClientSpend:
      build.query<api.ExternalClientSpendTimeSeriesResponse, api.ExternalClientSpendFilterParams>({
        async queryFn(params) {
          return await ExternalVendorStatisticsMutex.runExclusive(async () => {
            const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
            const data = await externalVendorApi.getExternalClientSpend(params);
            return { data };
          });
        },
        providesTags: ['ExternalVendors'],
      }),
    getExternalVendorPeriodStatistics:
      build.query<api.ExternalVendorPeriodStatisticsResponse, api.ExternalVendorPeriodStatisticsFilterParams>({
        async queryFn(params) {
          return await ExternalVendorStatisticsMutex.runExclusive(async () => {
            const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
            const data = await externalVendorApi.getExternalVendorPeriodStatistics(params);
            return { data };
          });
        },
      }),
    getExternalClientPeriodStatistics:
      build.query<api.IPagedResults<api.ExternalClientPeriodSpend>, api.ExternalClientPeriodStatisticsFilterParams>({
        async queryFn(params) {
          return await ExternalVendorStatisticsMutex.runExclusive(async () => {
            const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
            const data = await externalVendorApi.getExternalClientPeriodStatistics(params);
            return { data };
          });
        },
      }),
    getExternalVendorDimensions:
      build.query<api.ExternalVendorDimensionsResponse, api.ExternalVendorDimensionsFilters>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendorDimensions(params);
          return { data };
        },
        providesTags: ['ExternalVendorDimensions'],
      }),
    /** @deprecated Use getAllExternalVendorsV2 */
    getAllExternalVendors:
      build.query<api.IPagedResults<api.ExternalVendorDataResponse>, api.ExternalVendorFilterParams & { readonly partnerId: number }>({
        async queryFn({ partnerId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getAllExternalVendors(partnerId, params);
          return { data };
        },
        providesTags: ['ExternalVendors'],
      }),
    getAllExternalVendorsV2:
      build.query<api.IPagedResults<api.ExternalVendorDataResponse>, api.ExternalVendorFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getAllExternalVendorsV2(params);
          return { data };
        },
        providesTags: ['ExternalVendors'],
      }),
    getExternalVendor:
      build.query<api.ExternalVendorDataResponse | undefined, { readonly partnerId: number; readonly externalVendorId: string; readonly businessEntityScope: api.BusinessEntityScope }>({
        async queryFn({ partnerId, externalVendorId, businessEntityScope }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendor(partnerId, externalVendorId, { businessEntityScope });
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalVendor', ...id, ...result }]
          : ['ExternalVendor']),
      }),
    getExternalVendorClients:
      build.query<api.IPagedResults<api.ExternalVendorClientDataResponse>, api.ExternalVendorClientFilterParams & { readonly partnerId: number; readonly externalVendorId: string; }>({
        async queryFn({ partnerId, externalVendorId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendorClients(partnerId, externalVendorId, params);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalVendorClients', ...id, ...result }]
          : ['ExternalVendorClients']),
      }),
    getExternalVendorInvoices:
      build.query<api.IPagedResults<api.ExternalInvoiceDataResponse>, api.ExternalVendorInvoiceFilterParams & { readonly partnerId: number; readonly externalVendorId: string; }>({
        async queryFn({ partnerId, externalVendorId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendorInvoices(partnerId, externalVendorId, params);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalVendorInvoices', ...id, ...result }]
          : ['ExternalVendorInvoices']),
      }),
    getExternalVendorInvoicesByInternalVendorId:
      build.query<api.IPagedResults<api.ExternalInvoiceDataResponse>, api.ExternalVendorInvoiceByInternalVendorIdFilterParams & { readonly internalVendorId: string; }>({
        async queryFn({ internalVendorId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalVendorInvoicesByInternalVendorId(internalVendorId, params);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalVendorInvoices', ...id, ...result }]
          : ['ExternalVendorInvoices']),
      }),
    getAllExternalClients:
      build.query<api.IPagedResults<api.ExternalClientDataResponse>, api.ExternalClientFilterParams & { readonly partnerId: number }>({
        async queryFn({ partnerId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getAllExternalClients(partnerId, params);
          return { data };
        },
        providesTags: ['ExternalClients'],
      }),
    getExternalClients: build.query<readonly api.ExternalClientDataResponse[], { partnerId: api.Partners; externalClientIds: readonly string[] }>({
      async queryFn({ partnerId, externalClientIds }, _queryApi, _extraOptions, fetchWithBQ) {
        const responses = await Promise.all(externalClientIds.map((externalClientId) => fetchWithBQ({
          url: `/partners/${partnerId}/external-clients/${externalClientId}`,
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        })));
        return {
          data: responses.filter((r) => !!r.data).map((r) => r.data as api.ExternalClientDataResponse),
        };
      },
      providesTags: (result) => (result
        ? result.map((ecd) => ({ type: 'ExternalClient', ...ecd }))
        : []),
    }),
    getExternalClient:
      build.query<api.ExternalClientDataResponse | undefined, { readonly partnerId: number; readonly externalClientId: string; readonly businessEntityScope: api.BusinessEntityScope }>({
        async queryFn({ partnerId, externalClientId, businessEntityScope }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalClient(partnerId, externalClientId, { businessEntityScope });
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalClient', ...id, ...result }]
          : ['ExternalClient']),
      }),
    getExternalClientVendors:
      build.query<api.IPagedResults<api.ExternalClientVendorDataResponse>, api.ExternalVendorFilterParams & { readonly partnerId: number; readonly externalClientId: string; }>({
        async queryFn({ partnerId, externalClientId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalClientVendors(partnerId, externalClientId, params);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalClientVendors', ...id, ...result }]
          : ['ExternalClientVendors']),
      }),
    getAllExternalClientVendors:
      build.query<api.IPagedResults<api.ExternalClientVendorDataResponse>, api.ExternalVendorFilterParams>({
        async queryFn(params) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getAllExternalClientVendors(params);
          return { data };
        },
        providesTags: ['AllExternalClientVendors']
      }),
    getExternalClientInvoices:
      build.query<api.IPagedResults<api.ExternalInvoiceDataResponse>, api.ExternalClientInvoiceFilterParams & { readonly partnerId: number; readonly externalClientId: string; }>({
        async queryFn({ partnerId, externalClientId, ...params }) {
          const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
          const data = await externalVendorApi.getExternalClientInvoices(partnerId, externalClientId, params);
          return { data };
        },
        providesTags: (result, error, id) => (result
          ? [{ type: 'ExternalClientInvoices', ...id, ...result }]
          : ['ExternalClientInvoices']),
      }),
    getWaptDistribution:
      build.query<api.WAPTDistributionResponse, api.WAPTDistributionRequest>({
        async queryFn(params) {
          return await ExternalVendorStatisticsMutex.runExclusive(async () => {
            const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
            const data = await externalVendorApi.getWaptDistribution(params);
            return { data };
          });
        },
      }),
    getTaskSchedules: build.query<Record<string, string>, any>({
      async queryFn() {
        const externalPaymentApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalPaymentApi.getTaskSchedules<Record<string, string>>();
        return { data };
      },
      providesTags: ['TaskSchedules'],
    }),
    getAllTaskExecutions: build.query<api.IPagedResults<api.ExternalVendorOperationsTaskExecutionResponse>, api.ExternalVendorOperationsTaskExecutionFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.getAllOperationsTaskExecutions(params);
        return { data };
      },
      providesTags: ['TaskExecutions'],
    }),
    createTaskExecution: build.mutation<api.ExternalVendorOperationsTaskExecutionResponse, api.ExternalVendorOperationsTaskExecutionRequest>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.createOperationsTaskExecution(params);
        return { data };
      },
      invalidatesTags: ['TaskExecutions'],
    }),
    uploadExternalInvoices: build.mutation<void, { body: FormData }>({
      async queryFn({ body }) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.uploadExternalInvoices(body);
        return { data };
      },
    }),
    getMatchingDashboardInvoices: build.query<api.MatchingDashboardInvoiceListResponse, api.MatchingDashboardInvoiceFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.getMatchingDashboardInvoices(params);
        return { data };
      },
      providesTags: [{ type: 'ExternalVendorInvoices' }, { type: 'MatchingDashboard' }],
    }),
    getMatchingDashboardClients: build.query<readonly api.MatchingDashboardClientInfo[], { readonly vendorId: api.UUID }>({
      async queryFn({ vendorId }) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.getMatchingDashboardClients(vendorId);
        return { data };
      },
      providesTags: [{ type: 'MatchingDashboard' }],
    }),
    getMatchingDashboardMetrics: build.query<api.MatchingDashboardMetricsResponse, api.MatchingDashboardMetricsFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.getMatchingDashboardMetrics(params);
        return { data };
      },
      providesTags: [{ type: 'ExternalVendorInvoices' }, { type: 'MatchingDashboard' }],
    }),
    getMatchingDashboardTransactions: build.query<api.MatchingDashboardTransactionListResponse, api.MatchingDashboardTransactionFilterParams>({
      async queryFn(params) {
        const externalVendorApi = useApi((options) => new ExternalVendorApi(CONFIG.api.externalVendorUrl, options));
        const data = await externalVendorApi.getMatchingDashboardTransactions(params);
        return { data };
      },
      providesTags: [{ type: 'ExternalVendorInvoices' }, { type: 'MatchingDashboard' }],
    }),
  }),
});

export const {
  useGetExternalVendorSpendQuery,
  useLazyGetExternalVendorSpendQuery,
  useGetExternalVendorOnboardedSpendQuery,
  useLazyGetExternalVendorOnboardedSpendQuery,
  useGetExternalClientSpendQuery,
  useLazyGetExternalClientSpendQuery,
  useGetExternalVendorPeriodStatisticsQuery,
  useLazyGetExternalVendorPeriodStatisticsQuery,
  useGetExternalClientPeriodStatisticsQuery,
  useLazyGetExternalClientPeriodStatisticsQuery,
  useGetExternalVendorDimensionsQuery,
  useLazyGetExternalVendorDimensionsQuery,
  useGetAllExternalVendorsQuery,
  useLazyGetAllExternalVendorsQuery,
  useGetAllExternalVendorsV2Query,
  useLazyGetAllExternalVendorsV2Query,
  useGetExternalVendorQuery,
  useGetExternalVendorClientsQuery,
  useGetExternalVendorInvoicesQuery,
  useGetExternalVendorInvoicesByInternalVendorIdQuery,
  useGetAllExternalClientsQuery,
  useGetExternalClientsQuery,
  useGetExternalClientQuery,
  useGetExternalClientVendorsQuery,
  useGetExternalClientInvoicesQuery,
  useGetWaptDistributionQuery,
  useLazyGetWaptDistributionQuery,
  useGetAllExternalClientVendorsQuery,
  useGetTaskSchedulesQuery,
  useGetAllTaskExecutionsQuery,
  useCreateTaskExecutionMutation,
  useUploadExternalInvoicesMutation,
  useGetMatchingDashboardInvoicesQuery,
  useGetMatchingDashboardClientsQuery,
  useGetMatchingDashboardMetricsQuery,
  useLazyGetMatchingDashboardMetricsQuery,
  useGetMatchingDashboardTransactionsQuery,
} = externalVendorsApi;

/** @deprecated Use exportAllExternalVendorsV2 */
export const exportAllExternalVendors = async (partnerId: api.Partners, filterParams?: api.ExternalVendorFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/partners/${partnerId}/external-vendors/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllExternalVendorsV2 = async (filterParams?: api.ExternalVendorFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/external-vendors/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllExternalClients = async (partnerId: api.Partners, filterParams?: api.ExternalClientFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/partners/${partnerId}/external-clients/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportExternalClientVendors = async (partnerId: api.Partners, externalClientId: string, filterParams?: api.ExternalClientVendorFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/partners/${partnerId}/external-clients/${externalClientId}/external-vendors/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportAllExternalClientVendors = async (filterParams?: api.ExternalClientVendorFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/external-client-vendors/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportClientPeriodSpend = async (filterParams?: api.ExternalClientPeriodStatisticsFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/external-clients/statistics/period/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportExternalVendorInvoices = async (partnerId: api.Partners, externalVendorId: string, filterParams?: api.ExternalVendorInvoiceFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/partners/${partnerId}/external-vendors/${externalVendorId}/external-invoices/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};

export const exportExternalClientInvoices = async (partnerId: api.Partners, externalClientId: string, filterParams?: api.ExternalClientInvoiceFilterParams): Promise<BlobPart> => {
  const idToken = await getIdToken();

  const url = new URL(`${CONFIG.api.externalVendorUrl}/partners/${partnerId}/external-clients/${externalClientId}/external-invoices/csv/export`);
  appendFilterParams(url, filterParams);

  const response = await fetch(url.href, {
    mode: 'cors',
    cache: 'no-cache',
    credentials: 'same-origin',
    headers: idToken ? {
      Authorization: `Bearer ${idToken}`,
    } : undefined,
  });

  return await response.blob();
};